import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Spin } from 'antd';
import {
  DashboardPageShell,
  DashboardPanel,
  DashboardPrimaryActionButton,
} from './components/dashboard';
import { api } from './api/http';
import { useAuth } from './auth/AuthProvider';
import { customerUserRoleLabel } from './customerUserRoles';

import './styles/authOnboarding.css';

export default function InviteAcceptPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { session, login } = useAuth();

  const tokenFromUrl = (params.get('token') || '').trim();

  const token = useMemo(() => {
    if (tokenFromUrl) {
      try {
        localStorage.setItem('pendingInviteToken', tokenFromUrl);
      } catch {}
      return tokenFromUrl;
    }
    try {
      return (localStorage.getItem('pendingInviteToken') || '').trim();
    } catch {
      return '';
    }
  }, [tokenFromUrl]);

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [err, setErr] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  const meta = useMemo(() => {
    if (!invite?.metadata) return {};
    try {
      return JSON.parse(invite.metadata);
    } catch {
      return {};
    }
  }, [invite]);

  const loadInvite = useCallback(async () => {
    setLoading(true);
    setErr('');
    setInvite(null);

    if (!token) {
      try {
        localStorage.removeItem('pendingInviteToken');
      } catch {}
      setErr('Missing invite token. Please use the full invite link from your email.');
      setLoading(false);
      return;
    }

    try {
      const res = await api.get(`/api/public/signup/lookup?token=${encodeURIComponent(token)}`);
      setInvite(res.data);
    } catch (e) {
      try {
        localStorage.removeItem('pendingInviteToken');
      } catch {}
      const msg = e?.response?.data?.message || 'This invite could not be found or is no longer available.';
      setErr(msg);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadInvite();
  }, [loadInvite]);

  useEffect(() => {
    if (!invite) return;
    if (!invite.used && !invite.expired) return;
    try {
      localStorage.removeItem('pendingInviteToken');
    } catch {}
  }, [invite]);

  const startAuth = async () => {
    const returnTo = `/invite?token=${encodeURIComponent(token)}`;
    const authorizationParams = {
      login_hint: invite?.email || undefined,
      ...(invite?.existingUser ? {} : { screen_hint: 'signup' }),
    };

    await login({
      appState: { returnTo },
      authorizationParams,
    });
  };

  const routeHomeFor = (role) => {
    const r = String(role || '').toUpperCase();
    if (r === 'OPERATOR') return '/operator';
    if (r === 'TECHNICIAN') return '/technician-dashboard';
    if (r === 'CUSTOMER') return '/fleet_dashboard';
    return '/';
  };

  const acceptInvite = useCallback(async () => {
    if (!token || accepting || accepted) return;
    if (!invite || invite.used || invite.expired) return;

    setAccepting(true);
    setErr('');

    try {
      const res = await api.post('/api/signup/accept', {
        token,
        name: meta?.full_name || '',
      });

      const data = res.data || {};
      const role = String(data.role || invite.role || '').toUpperCase();
      const customerId = data.customerId ?? invite.customerId ?? null;
      const userId = data.userId ?? null;

      try {
        localStorage.setItem(
          'session',
          JSON.stringify({
            userId,
            role,
            customerId,
          })
        );
        if (customerId != null) {
          localStorage.setItem('customerId', String(customerId));
          localStorage.setItem('selectedCustomerId', String(customerId));
          localStorage.removeItem('pendingCustomerId');
        }
      } catch {}

      setAccepted(true);

      try {
        localStorage.removeItem('pendingInviteToken');
      } catch {}

      navigate(routeHomeFor(role), { replace: true });
    } catch (e) {
      const msg = e?.response?.data?.message || 'Failed to accept invite.';
      setErr(msg);
    } finally {
      setAccepting(false);
    }
  }, [token, invite, accepting, accepted, navigate, meta]);

  useEffect(() => {
    if (!invite) return;
    if (invite.used || invite.expired) return;
    if (!session) return;
    acceptInvite();
  }, [invite, session, acceptInvite]);

  const titleRole = customerUserRoleLabel(invite?.role);
  const status = invite?.used ? 'Used' : invite?.expired ? 'Expired' : 'Ready';

  return (
    <DashboardPageShell
      title="Accept Invite"
      productDashboardShell="customer"
      headerClassName="dashboard-fleet-header"
      contentClassName="dashboard-fleet-page auth-onboarding-page auth-invite-page"
      contentInnerClassName="auth-onboarding-shell"
      shellClassName="auth-onboarding-page-shell"
    >
      <DashboardPanel className="dashboard-fleet-dashboard-panel auth-onboarding-panel">
        <header className="auth-onboarding-header">
          <p className="auth-onboarding-eyebrow">ManageLift invite</p>
          <h1 className="auth-onboarding-title">{loading ? 'Checking your invite' : 'Accept your invitation'}</h1>
          <p className="auth-onboarding-subtitle">
            {loading
              ? 'We are confirming the invite details before continuing.'
              : 'Review the invitation details, then continue to join the customer account.'}
          </p>
        </header>

        <div className="auth-onboarding-content">
          <div className="auth-onboarding-section">
            {loading && (
              <div className="auth-onboarding-loading">
                <Spin />
                <span>Loading invite details…</span>
              </div>
            )}

            {!loading && err && (
              <div className="auth-onboarding-alert auth-onboarding-alert-error">{err}</div>
            )}

            {!loading && invite && !err && (
              <>
                <div className="auth-onboarding-summary" aria-label="Invite summary">
                  <div className="auth-onboarding-summary-row">
                    <span className="auth-onboarding-summary-label">Invited email</span>
                    <span className="auth-onboarding-summary-value">{invite.email || '(unknown)'}</span>
                  </div>
                  <div className="auth-onboarding-summary-row">
                    <span className="auth-onboarding-summary-label">Role</span>
                    <span className="auth-onboarding-summary-value">{titleRole || '(unknown)'}</span>
                  </div>
                  <div className="auth-onboarding-summary-row">
                    <span className="auth-onboarding-summary-label">Status</span>
                    <span className="auth-onboarding-summary-value">{status}</span>
                  </div>
                </div>

                {(invite.used || invite.expired) && (
                  <div className="auth-onboarding-alert auth-onboarding-alert-warning">
                    This invite can’t be accepted. Ask your customer administrator for a new invite if you still need access.
                  </div>
                )}

                <div className="auth-onboarding-actions">
                  {!invite.used && !invite.expired && !session && (
                    <DashboardPrimaryActionButton
                      size="large"
                      onClick={startAuth}
                      className="auth-onboarding-primary"
                    >
                      Continue
                    </DashboardPrimaryActionButton>
                  )}

                  {!invite.used && !invite.expired && session && (
                    <DashboardPrimaryActionButton
                      size="large"
                      loading={accepting}
                      onClick={acceptInvite}
                      className="auth-onboarding-primary"
                    >
                      Accept Invite
                    </DashboardPrimaryActionButton>
                  )}

                  <Button
                    size="large"
                    onClick={() => {
                      try {
                        localStorage.removeItem('pendingInviteToken');
                      } catch {}
                      navigate('/login', { replace: true });
                    }}
                    className="dashboard-secondary-action-button auth-onboarding-secondary"
                  >
                    Back to login
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </DashboardPanel>
    </DashboardPageShell>
  );
}
